/**
 * Search Results JavaScript
 * Dynamic filtering, sorting, and search functionality
 */

(function() {
  'use strict';

  class SearchResults {
    constructor() {
      this.section = document.querySelector('[data-search-results]');
      if (!this.section) return;

      this.filterToggle = this.section.querySelector('[data-filter-toggle]');
      this.filterPanel = this.section.querySelector('[data-filter-panel]');
      this.filterTypes = this.section.querySelectorAll('[data-filter-type]');
      this.sortSelect = this.section.querySelector('[data-search-sort]');
      this.searchForm = this.section.querySelector('.search-results__form');
      
      this.init();
    }

    init() {
      // Initialize filter toggle
      if (this.filterToggle && this.filterPanel) {
        this.initFilters();
      }

      // Initialize sort
      if (this.sortSelect) {
        this.initSort();
      }

      // Initialize filter checkboxes
      if (this.filterTypes.length > 0) {
        this.initFilterTypes();
      }
    }

    initFilters() {
      this.filterToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const isOpen = this.filterPanel.getAttribute('data-open') === 'true';
        
        if (isOpen) {
          this.closeFilterPanel();
        } else {
          this.openFilterPanel();
        }
      });

      // Close filter panel when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.filterPanel.contains(e.target) && 
            !this.filterToggle.contains(e.target)) {
          this.closeFilterPanel();
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.filterPanel.getAttribute('data-open') === 'true') {
          this.closeFilterPanel();
        }
      });
    }

    openFilterPanel() {
      this.filterPanel.setAttribute('data-open', 'true');
      this.filterToggle.setAttribute('aria-expanded', 'true');
    }

    closeFilterPanel() {
      this.filterPanel.setAttribute('data-open', 'false');
      this.filterToggle.setAttribute('aria-expanded', 'false');
    }

    initFilterTypes() {
      this.filterTypes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.applyFilters();
        });
      });
    }

    applyFilters() {
      const selectedTypes = Array.from(this.filterTypes)
        .filter(cb => cb.checked)
        .map(cb => cb.value);

      if (selectedTypes.length === 0) {
        // If no types selected, show all
        this.showAllResults();
        return;
      }

      // Hide/show results based on selected types
      const results = this.section.querySelectorAll('[data-search-item]');
      
      results.forEach(item => {
        const itemType = item.classList.contains('search-results__item--product') ? 'product' :
                        item.classList.contains('search-results__item--collection') ? 'collection' :
                        item.classList.contains('search-results__item--article') ? 'article' :
                        item.classList.contains('search-results__item--page') ? 'page' : '';
        
        if (selectedTypes.includes(itemType)) {
          item.style.display = '';
          item.style.animation = 'fadeInUp 0.5s ease-out';
        } else {
          item.style.display = 'none';
        }
      });

      // Update URL with filter parameters
      this.updateURL(selectedTypes);
    }

    showAllResults() {
      const results = this.section.querySelectorAll('[data-search-item]');
      results.forEach(item => {
        item.style.display = '';
        item.style.animation = 'fadeInUp 0.5s ease-out';
      });
    }

    initSort() {
      this.sortSelect.addEventListener('change', () => {
        this.applySort();
      });
    }

    applySort() {
      const sortValue = this.sortSelect.value;
      const resultsGrid = this.section.querySelector('[data-search-results-grid]');
      const results = Array.from(this.section.querySelectorAll('[data-search-item]'));

      // Sort results based on selected option
      results.sort((a, b) => {
        if (sortValue === 'price-ascending') {
          const priceA = this.getProductPrice(a);
          const priceB = this.getProductPrice(b);
          return priceA - priceB;
        } else if (sortValue === 'price-descending') {
          const priceA = this.getProductPrice(a);
          const priceB = this.getProductPrice(b);
          return priceB - priceA;
        } else {
          // Relevance - keep original order
          return 0;
        }
      });

      // Re-append sorted results
      results.forEach((item, index) => {
        resultsGrid.appendChild(item);
        item.style.animationDelay = `${index * 0.1}s`;
      });

      // Update URL
      this.updateURL(null, sortValue);
    }

    getProductPrice(item) {
      const priceElement = item.querySelector('.search-results__product-price-current');
      if (!priceElement) return 0;
      
      const priceText = priceElement.textContent.trim();
      // Extract number from price (remove currency symbols, commas, etc.)
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (!priceMatch) return 0;
      
      return parseFloat(priceMatch[0].replace(/,/g, ''));
    }

    updateURL(types, sort) {
      const url = new URL(window.location);
      
      if (types && types.length > 0) {
        url.searchParams.set('type', types.join(','));
      } else {
        url.searchParams.delete('type');
      }
      
      if (sort) {
        url.searchParams.set('sort_by', sort);
      } else {
        url.searchParams.delete('sort_by');
      }
      
      // Update URL without reload (for better UX)
      window.history.replaceState({}, '', url);
    }

    // Handle form submission with filters
    handleFormSubmit(e) {
      if (this.filterTypes.length > 0) {
        const selectedTypes = Array.from(this.filterTypes)
          .filter(cb => cb.checked)
          .map(cb => cb.value);
        
        if (selectedTypes.length > 0) {
          const typeInput = document.createElement('input');
          typeInput.type = 'hidden';
          typeInput.name = 'type';
          typeInput.value = selectedTypes.join(',');
          e.target.appendChild(typeInput);
        }
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new SearchResults();
    });
  } else {
    new SearchResults();
  }

  // Re-initialize on section load (for Shopify theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId) {
        const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
        if (section && section.hasAttribute('data-search-results')) {
          new SearchResults();
        }
      }
    });
  }
})();
